import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tv, Search, Plus, Copy, MoreHorizontal, Pencil, Power, Trash2, CalendarClock } from 'lucide-react';
import { Container, PageHeader, Toolbar, Breadcrumbs, BreadcrumbItem } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  Input,
  Skeleton,
  IconButton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useChannels,
  useFastChannelServices,
  useSetChannelStatus,
  useDeleteChannel,
  type Channel,
} from '@/api/fastchannel';
import { trackFeatureAction } from '@/lib/analytics-events';
import { useDebounce } from '../shared';
import { channelM3u8Url, playModeLabel, isChannelDisabled } from './shared';
import { ChannelStatusPill } from './ChannelStatusPill';
import { CreateChannelDialog } from './CreateChannelDialog';
import { EditChannelDialog } from './EditChannelDialog';

const BASE = '/services/fastchannel';

export function ChannelsDashboard() {
  const navigate = useNavigate();
  const { serviceUuid } = useParams<{ serviceUuid: string }>();
  const { show } = useToast();

  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 300).trim().toLowerCase();
  const [addOpen, setAddOpen] = useState(false);
  const [editChannel, setEditChannel] = useState<Channel | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Channel | null>(null);

  const { data: services = [] } = useFastChannelServices();
  const service = services.find((s) => s.id === serviceUuid);
  const { data: channels = [], isLoading } = useChannels(serviceUuid);

  const setStatus = useSetChannelStatus(serviceUuid ?? '', {
    onSuccess: () => show({ tone: 'success', title: 'Channel updated' }),
    onError: () => show({ tone: 'danger', title: 'Could not update channel' }),
  });
  const remove = useDeleteChannel(serviceUuid ?? '', {
    onSuccess: () => {
      show({ tone: 'success', title: 'Channel deleted' });
      trackFeatureAction('fastchannel', 'channel', 'delete');
      setPendingDelete(null);
    },
    onError: () => show({ tone: 'danger', title: 'Could not delete channel' }),
  });

  const rows = useMemo<Channel[]>(() => {
    if (!search) return channels;
    return channels.filter(
      (c) =>
        c.name?.toLowerCase().includes(search) ||
        c.title?.toLowerCase().includes(search) ||
        c.tenant?.toLowerCase().includes(search),
    );
  }, [channels, search]);

  const copyM3u8 = (channelId: string) => {
    void navigator.clipboard?.writeText(channelM3u8Url(channelId));
    show({ tone: 'success', title: 'Stream URL copied' });
  };

  const columns = useMemo<ColumnDef<Channel, unknown>[]>(
    () => [
      {
        id: 'name',
        header: 'Key',
        cell: ({ row }) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Tv size={14} color="var(--vn-fg-muted)" />
            <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.name}</span>
            <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
              <IconButton
                variant="ghost"
                size="sm"
                aria-label="Copy stream URL"
                onClick={() => copyM3u8(row.original.id)}
              >
                <Copy size={14} />
              </IconButton>
            </span>
          </span>
        ),
      },
      {
        id: 'title',
        header: 'Title',
        cell: ({ row }) => row.original.title || '—',
      },
      {
        id: 'tenant',
        header: 'Tenant',
        enableSorting: false,
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.tenant || '—'}</span>
        ),
      },
      {
        id: 'mode',
        header: 'Play mode',
        enableSorting: false,
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-muted)' }}>{playModeLabel(row.original)}</span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => <ChannelStatusPill status={row.original.status} />,
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const ch = row.original;
          const disabled = isChannelDisabled(ch.status);
          return (
            <div style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <IconButton variant="ghost" size="sm" aria-label="Channel actions">
                    <MoreHorizontal size={16} />
                  </IconButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onSelect={() => navigate(`${BASE}/${serviceUuid}/channels/${ch.id}/schedules`)}
                  >
                    <CalendarClock size={14} /> Manage schedule
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setEditChannel(ch)}>
                    <Pencil size={14} /> Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() =>
                      setStatus.mutate(
                        { channelUuid: ch.id, status: disabled ? 'active' : 'inactive' },
                        { onSuccess: () => trackFeatureAction('fastchannel', 'channel', disabled ? 'enable' : 'disable') },
                      )
                    }
                  >
                    <Power size={14} /> {disabled ? 'Enable' : 'Disable'}
                  </DropdownMenuItem>
                  <DropdownMenuItem tone="danger" onSelect={() => setPendingDelete(ch)}>
                    <Trash2 size={14} /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serviceUuid],
  );

  return (
    <Container size="xl">
      <PageHeader
        icon={<Tv size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem href={BASE} onClick={(e) => { e.preventDefault(); navigate(BASE); }}>FastChannel</BreadcrumbItem>
            <BreadcrumbItem current>{service?.service_name || 'Channels'}</BreadcrumbItem>
          </Breadcrumbs>
        }
        title={service?.service_name || 'Channels'}
        description="Channels are activated FAST playout services. Click a row to manage its schedule."
        actions={
          <Button startIcon={<Plus size={15} />} onClick={() => setAddOpen(true)}>
            Add channel
          </Button>
        }
      />

      <Toolbar
        start={
          <Input
            placeholder="Find channels by key, title or tenant"
            startSlot={<Search />}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ maxWidth: 340 }}
          />
        }
      />

      {isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 48 }} />
          ))}
        </div>
      ) : channels.length === 0 ? (
        <EmptyState
          icon={<Tv size={20} />}
          title="No channels yet"
          description="Create your first FAST channel to start scheduling playout."
          actions={
            <Button startIcon={<Plus size={15} />} onClick={() => setAddOpen(true)}>
              Add channel
            </Button>
          }
        />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => r.id}
          onRowClick={(r) => navigate(`${BASE}/${serviceUuid}/channels/${r.id}/schedules`)}
          emptyState="No channels match your search."
        />
      )}

      {serviceUuid && (
        <CreateChannelDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          serviceUuid={serviceUuid}
          tenant={channels[0]?.tenant}
          onCreated={(id) =>
            navigate(`${BASE}/${serviceUuid}/channels/${id}/schedules`)
          }
        />
      )}

      {serviceUuid && editChannel && (
        <EditChannelDialog
          open
          onOpenChange={(o) => !o && setEditChannel(null)}
          serviceUuid={serviceUuid}
          channel={editChannel}
        />
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete channel?"
        description={`This permanently removes “${pendingDelete?.title || pendingDelete?.name}” and all its schedules. This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        requireText={pendingDelete?.name}
        loading={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
      />
    </Container>
  );
}
